// OSRM HTTP client — singleton con timeout, circuit breaker y bus de eventos.
// Nunca lanza: retorna `null` en fallo para que los callers no necesiten try/catch.
//
// Consumido por: views/MapV2.vue (onHoverNearby, onClickNearby, onNearbyAtPoint)
// Dependencias: ninguna fuera de Vue (solo para el event bus) y osrm-constants.js

import Vue from 'vue'
import {
  OSRM_BASE_URL,
  OSRM_REQUEST_TIMEOUT_MS,
  CIRCUIT_FAIL_THRESHOLD,
  CIRCUIT_WINDOW_MS,
  CIRCUIT_OPEN_MS,
  OSRM_SNAP_RADIUS_M,
} from '../utils/osrm-constants'

const STATUS_EVENT = 'osrm-status-changed'

// Respuestas de OSRM que significan "no hay ruta entre estos puntos" (fuera
// del grafo, sin conexión vial) y no "el servicio está caído". No cuentan
// para el circuit breaker: tres unidades lejanas no deben apagar el ruteo.
const UNROUTABLE_CODES = ['NoSegment', 'NoRoute']

function UnroutableError(reason) {
  this.message = reason
}

function OsrmService() {
  if (OsrmService.instance) return OsrmService.instance
  OsrmService.instance = this

  this._recentFailures = []
  this._circuitOpenUntil = 0
  this._circuitResetTimer = null
  this._eventBus = new Vue()

  this._metrics = {
    totalRequests: 0,
    successCount: 0,
    failCount: 0,
    avgResponseMs: 0,
    _totalResponseMs: 0,
    circuitState: 'closed',
    lastFailAt: null,
    lastFailReason: null,
  }
  window.__osrmMetrics = this._metrics

  return this
}

OsrmService.prototype.onStatusChange = function (handler) {
  const self = this
  self._eventBus.$on(STATUS_EVENT, handler)
  return function () { self._eventBus.$off(STATUS_EVENT, handler) }
}

OsrmService.prototype.route = function (origin, destination) {
  const self = this
  if (self._isCircuitOpen()) return Promise.resolve(null)
  if (!Array.isArray(origin) || !Array.isArray(destination)) return Promise.resolve(null)
  if (origin.length < 2 || destination.length < 2) return Promise.resolve(null)

  const url = OSRM_BASE_URL
    + '/route/v1/driving/'
    + origin[0] + ',' + origin[1] + ';'
    + destination[0] + ',' + destination[1]
    + '?geometries=geojson&overview=full&alternatives=false&steps=false'
    + '&radiuses=' + OSRM_SNAP_RADIUS_M + ';' + OSRM_SNAP_RADIUS_M

  const startedAt = performance.now()
  self._metrics.totalRequests++

  return self._fetchWithTimeout(url, OSRM_REQUEST_TIMEOUT_MS)
    .then((resp) => {
      // NoSegment/NoRoute llegan con HTTP 400: hay que leer el body antes de
      // decidir, o se confunden con una caída del servidor.
      return resp.json().catch(() => null).then((data) => {
        if (data && UNROUTABLE_CODES.indexOf(data.code) !== -1) {
          throw new UnroutableError('OSRM code: ' + data.code)
        }
        if (!resp.ok) throw new Error('HTTP ' + resp.status)
        return data
      })
    })
    .then((data) => {
      if (!data || data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
        throw new Error('OSRM code: ' + ((data && data.code) || 'unknown'))
      }
      // Segunda defensa por si el servidor ignora `radiuses`: un waypoint
      // pegado más lejos que el radio es un origen/destino que no es el real.
      const snappedTooFar = (data.waypoints || []).some((w) => w.distance > OSRM_SNAP_RADIUS_M)
      if (snappedTooFar) throw new UnroutableError('OSRM snap > ' + OSRM_SNAP_RADIUS_M + ' m')

      const r = data.routes[0]
      const elapsed = performance.now() - startedAt
      self._onSuccess(elapsed)
      return {
        geometry: r.geometry,
        distance: r.distance,
        duration: r.duration,
      }
    })
    .catch((err) => {
      if (err instanceof UnroutableError) {
        self._onUnroutable(err, performance.now() - startedAt)
      } else {
        self._onFailure(err)
      }
      return null
    })
}

OsrmService.prototype.routeBatch = function (origin, destinations) {
  const self = this
  if (!Array.isArray(destinations) || destinations.length === 0) return Promise.resolve([])
  const promises = destinations.map((d) => {
    return self.route(origin, d.coordinates).then((route) => {
      return { id: d.id, route }
    })
  })
  return Promise.all(promises)
}

OsrmService.prototype.reset = function () {
  this._recentFailures = []
  this._setCircuitOpenUntil(0)
  this._metrics.circuitState = 'closed'
  this._emitStatus({ available: true, reason: 'manual-reset' })
}

OsrmService.prototype._isCircuitOpen = function () {
  return Date.now() < this._circuitOpenUntil
}

OsrmService.prototype._onSuccess = function (elapsedMs) {
  this._metrics.successCount++
  this._metrics._totalResponseMs += elapsedMs
  this._metrics.avgResponseMs = Math.round(
    this._metrics._totalResponseMs / this._metrics.successCount
  )
  if (this._recentFailures.length > 0 || this._isCircuitOpen()) {
    this._recentFailures = []
    const wasOpen = this._isCircuitOpen()
    this._setCircuitOpenUntil(0)
    if (wasOpen) {
      this._metrics.circuitState = 'closed'
      this._emitStatus({ available: true, reason: 'success-after-fail' })
    }
  }
}

// El servidor respondió bien pero no hay ruta entre los puntos. Cuenta como
// señal de salud (cierra el breaker si estaba abierto), no como fallo.
OsrmService.prototype._onUnroutable = function (err, elapsedMs) {
  this._metrics.unroutableCount = (this._metrics.unroutableCount || 0) + 1
  this._metrics.lastUnroutableReason = err.message
  this._onSuccess(elapsedMs)
}

OsrmService.prototype._onFailure = function (err) {
  const now = Date.now()
  const reason = (err && err.message) || 'unknown'
  this._metrics.failCount++
  this._metrics.lastFailAt = now
  this._metrics.lastFailReason = reason

  this._recentFailures = this._recentFailures.filter((f) => {
    return now - f.at < CIRCUIT_WINDOW_MS
  })
  this._recentFailures.push({ at: now })

  if (this._recentFailures.length >= CIRCUIT_FAIL_THRESHOLD && !this._isCircuitOpen()) {
    this._setCircuitOpenUntil(now + CIRCUIT_OPEN_MS)
    this._metrics.circuitState = 'open'
    this._emitStatus({ available: false, reason })

    const self = this
    this._circuitResetTimer = setTimeout(() => {
      self._circuitResetTimer = null
      if (!self._isCircuitOpen()) return
      self._setCircuitOpenUntil(0)
      self._recentFailures = []
      self._metrics.circuitState = 'closed'
      self._emitStatus({ available: true, reason: 'circuit-auto-reset' })
    }, CIRCUIT_OPEN_MS)
  }
}

OsrmService.prototype._setCircuitOpenUntil = function (epochMs) {
  this._circuitOpenUntil = epochMs
  if (epochMs === 0 && this._circuitResetTimer) {
    clearTimeout(this._circuitResetTimer)
    this._circuitResetTimer = null
  }
}

OsrmService.prototype._emitStatus = function (status) {
  this._eventBus.$emit(STATUS_EVENT, status)
}

OsrmService.prototype._fetchWithTimeout = function (url, ms) {
  return new Promise(((resolve, reject) => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      ctrl.abort()
      reject(new Error('OSRM timeout ' + ms + 'ms'))
    }, ms)

    fetch(url, { signal: ctrl.signal, credentials: 'same-origin' })
      .then((resp) => { clearTimeout(timer); resolve(resp) })
      .catch((err) => { clearTimeout(timer); reject(err) })
  }))
}

const instance = new OsrmService()
export default instance
export { OsrmService }
